import Phaser from 'phaser';
import { tokens } from '../design/tokens';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT, RENDER_SCALE } from '../dimensions';
import { audio } from '../game/Audio';
import { createBackground } from '../game/Background';
import { canInstall, promptInstall } from '../ui/InstallPrompt';
import { gameState } from '../state/GameState';
import { BURN_EVENT, type BurnTransitionPayload } from './BurnTransitionScene';
import { buildShareImage } from '../share/buildShareImage';
import { sharePayloadImage } from '../share/sharePayloadImage';
import { createMuteButton } from '../ui/MuteButton';

export class TitleScene extends Phaser.Scene {
  private creditsText!: Phaser.GameObjects.Text;
  private ctaText!: Phaser.GameObjects.Text;
  private sharePayloadPromise: Promise<Blob | null> | null = null;

  constructor() { super('Title'); }

  create(): void {
    this.cameras.main.setOrigin(0, 0).setZoom(RENDER_SCALE);
    createBackground(this);

    const playZone = this.add.rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    playZone.on('pointerdown', () => this.onPlayTap());

    const title = this.add.text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.34, 'LAVASCEND', {
      fontFamily: tokens.type.wordmark.family,
      fontSize: `${tokens.type.wordmark.size}px`,
      color: tokens.colors.ui.text
    }).setOrigin(0.5).setLetterSpacing(tokens.type.wordmark.letterSpacing);

    this.creditsText = this.add.text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.43, this.creditsLine(), {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size}px`,
      color: tokens.colors.ui.text,
      fontStyle: '700'
    }).setOrigin(0.5).setLetterSpacing(2);

    this.ctaText = this.add.text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.51, this.ctaLine(), {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size}px`,
      color: tokens.colors.ui.textMuted
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.ctaText.on('pointerdown', () => this.onPlayTap());

    const installBtn = this.add.text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.62, 'install app', {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size}px`,
      color: tokens.colors.ui.accent
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setVisible(canInstall());

    installBtn.on('pointerdown', async (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      audio.init();
      audio.uiTap();
      const result = await promptInstall();
      if (result === 'accepted' || result === 'unavailable') installBtn.setVisible(false);
    });

    const onAvailable = () => installBtn.setVisible(true);
    const onInstalled = () => installBtn.setVisible(false);
    window.addEventListener('lavascend:install-available', onAvailable);
    window.addEventListener('lavascend:installed', onInstalled);

    const onTokens = () => this.refreshTokens();
    this.game.events.on('lavascend:tokens', onTokens);

    const onSpace = (): void => this.onPlayTap();
    this.input.keyboard?.on('keydown-SPACE', onSpace);

    this.events.once('shutdown', () => {
      window.removeEventListener('lavascend:install-available', onAvailable);
      window.removeEventListener('lavascend:installed', onInstalled);
      this.game.events.off('lavascend:tokens', onTokens);
      this.input.keyboard?.off('keydown-SPACE', onSpace);
    });

    // Matches HudScene placement — below the BEST chip on the right edge,
    // out from under center-top punch-hole cameras.
    createMuteButton(this, LOGICAL_WIDTH - 48, 56);

    this.tweens.add({ targets: title, alpha: { from: 1, to: 0.6 }, duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });

    this.renderLeaderboard();
  }

  private creditsLine(): string { return `CREDITS: ${gameState.wallet.count}`; }
  private ctaLine(): string {
    return gameState.wallet.count > 0 ? 'tap to play' : 'tap to get tokens';
  }

  private refreshTokens(): void {
    this.creditsText.setText(this.creditsLine());
    this.ctaText.setText(this.ctaLine());
    const pulseMs = tokens.motion.tokenMachine.counterPulseMs;
    this.tweens.add({
      targets: this.creditsText,
      scale: { from: 1, to: 1.15 },
      duration: pulseMs / 2,
      yoyo: true,
      ease: tokens.motion.ease.emphasized
    });
  }

  private onPlayTap(): void {
    if (gameState.wallet.count <= 0) {
      audio.init();
      audio.panelOpen();
      this.openMachine();
      return;
    }
    // Spend pulse.
    this.tweens.add({
      targets: this.creditsText,
      scale: { from: 1, to: 0.85 },
      duration: tokens.motion.tokenMachine.counterPulseMs / 2,
      yoyo: true,
      ease: tokens.motion.ease.emphasized
    });
    gameState.wallet.spend();
    this.creditsText.setText(this.creditsLine());
    this.ctaText.setText(this.ctaLine());
    this.game.events.emit('lavascend:tokens');

    audio.init();
    audio.music();
    audio.start();
    const payload: BurnTransitionPayload = {
      onMidpoint: () => this.scene.start('Game'),
    };
    this.game.events.emit(BURN_EVENT, payload);
  }

  private openMachine(): void {
    if (this.scene.isActive('TokenMachine')) return;
    this.scene.launch('TokenMachine', { onClose: () => { /* Title remains; events handle refresh */ } });
  }

  private renderLeaderboard(): void {
    const state = gameState;
    const headerY = LOGICAL_HEIGHT * 0.78;
    const rowHeight = 16;

    this.add.text(LOGICAL_WIDTH / 2, headerY, 'HIGH SCORES', {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size}px`,
      color: tokens.colors.ui.text,
      fontStyle: 'bold',
    }).setOrigin(0.5).setLetterSpacing(2);

    if (state.scores.length === 0) {
      this.add.text(LOGICAL_WIDTH / 2, headerY + rowHeight + 4, 'no runs yet — tap to play.', {
        fontFamily: tokens.type.ui.family,
        fontSize: `${tokens.type.ui.size}px`,
        color: tokens.colors.ui.textMuted,
      }).setOrigin(0.5);
      return;
    }

    for (let i = 0; i < state.scores.length; i++) {
      const s = state.scores[i];
      const rank = String(i + 1).padStart(2, '0');
      const line = `${rank}.  L. ${s.level}   ${s.date}`;
      this.add.text(LOGICAL_WIDTH / 2, headerY + rowHeight + 4 + i * rowHeight, line, {
        fontFamily: tokens.type.ui.family,
        fontSize: `${tokens.type.ui.size}px`,
        color: tokens.colors.ui.textMuted,
      }).setOrigin(0.5);
    }

    if (state.scores.length > 0) {
      const top = state.scores[0];

      // Pre-build the PNG now so the share-button pointerdown can call
      // navigator.share() with minimal delay. The Web Share API requires
      // "transient user activation" which expires ~5s after the tap and is
      // also affected by long async chains — generating the image on demand
      // can blow that budget on mobile (cold font load + canvas.toBlob).
      this.sharePayloadPromise = buildShareImage({
        level: top.level,
        date: top.date,
        urlBase: window.location.host || 'lavascend.app',
      });

      const topY = headerY + rowHeight + 4 + 0 * rowHeight;
      const shareBtn = this.add.text(LOGICAL_WIDTH / 2 + 110, topY, '[ share ]', {
        fontFamily: tokens.type.ui.family,
        fontSize: `${tokens.type.ui.size}px`,
        color: tokens.colors.ui.accent,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      shareBtn.on('pointerdown', async (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        audio.uiTap();
        const blob = await (this.sharePayloadPromise ?? Promise.resolve(null));
        if (!blob) {
          this.flashToast('share unavailable');
          return;
        }
        const result = await sharePayloadImage(blob);
        if (result === 'copied') this.flashToast('copied to clipboard');
        else if (result === 'downloaded') this.flashToast('image saved');
        else if (result === 'failed') this.flashToast('share unavailable');
        // 'shared' is silent — the OS sheet already gave feedback.
      });
    }
  }

  private flashToast(message: string): void {
    const toast = this.add.text(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.95, message, {
      fontFamily: tokens.type.ui.family,
      fontSize: `${tokens.type.ui.size}px`,
      color: tokens.colors.ui.text,
      backgroundColor: '#2A0E3D',
      padding: { left: 8, right: 8, top: 4, bottom: 4 },
    }).setOrigin(0.5).setAlpha(0).setDepth(100);

    this.tweens.add({
      targets: toast,
      alpha: { from: 0, to: 1 },
      duration: 120,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.time.delayedCall(1000, () => {
          this.tweens.add({
            targets: toast,
            alpha: { from: 1, to: 0 },
            duration: 200,
            ease: 'Sine.easeIn',
            onComplete: () => toast.destroy(),
          });
        });
      },
    });
  }
}
